// web-v2/src/lib/date-display.js
//
// THE date display canon for the broker app (prompt 185).
//
// THE BUG THIS EXISTS TO KILL: `new Date("2026-08-21")` parses as UTC
// MIDNIGHT, which renders as 20 August in every timezone west of Greenwich.
// Not intermittently. Always. Postgres `date` columns arrive as exactly that
// string, so every surface that fed one to `new Date` has been a day early.
//
// THE RULE, and the whole point is that CALLERS DO NOT HAVE TO KNOW WHICH
// THEY HOLD:
//
//   A CALENDAR DATE ("2026-08-21", "20260821") is a day, not an instant. It
//   is parsed by SPLITTING THE STRING and constructing locally, and rendered
//   with NO timeZone option. Both halves matter. Constructing locally and
//   then rendering through America/New_York would re-break it for any viewer
//   east of Eastern, whose local midnight is the previous day in New York.
//   Local construction plus local rendering round-trips the components
//   exactly, in every zone, which is the only property that actually holds.
//
//   AN INSTANT (anything carrying a time, an offset, or a Z) is a moment. It
//   is parsed by `new Date`, which is correct for offset-bearing strings, and
//   rendered through America/New_York per the `human_timestamps` convention
//   already registered in facts/registry.js.
//
// WHY SHAPE-SNIFFING RATHER THAN A FLAG AT EACH CALL SITE: the same logical
// fact arrives in different shapes from different endpoints. Measured live
// 2026-08-18: `policy_expiration` is `2026-08-31T00:01:00-04:00` from
// /api/tracon/deal/:id and from /api/tracon/pipeline, but the transactions
// endpoint returns `effective_date` as a bare `2025-08-31`. A per-call-site
// flag would be wrong the first time an endpoint changed shape. The value
// knows what it is; the call site does not.
//
// NOT IN SCOPE, DELIBERATELY: the Zoho boundary. Zoho mixes date and datetime
// fields and punishes a wrong shape silently. Nothing here reads or writes a
// CRM field. Guard: tests/date-display-canon.test.js.

export const EASTERN = 'America/New_York';

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const COMPACT = /^(\d{4})(\d{2})(\d{2})$/;

const SHORT = { month: 'short', day: 'numeric' };
const HUMAN = { month: 'short', day: 'numeric', year: 'numeric' };

/**
 * Decide what a value IS before deciding how to render it.
 * Returns { kind: 'empty' | 'invalid' | 'calendar' | 'instant', date? }.
 *
 * Exported because the guard asserts on it directly: a classifier that can be
 * tested is a classifier whose 'calendar' branch can be proven to never route
 * through `new Date(string)`.
 */
export function classifyDateInput(value) {
  if (value === null || value === undefined || value === '') return { kind: 'empty' };

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? { kind: 'invalid' } : { kind: 'instant', date: value };
  }
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? { kind: 'invalid' } : { kind: 'instant', date: d };
  }

  const s = String(value).trim();
  if (!s) return { kind: 'empty' };

  const m = DATE_ONLY.exec(s) || COMPACT.exec(s);
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    // Reject impossible components before constructing, so "2026-13-45" is
    // invalid rather than silently rolling into the following year.
    if (mo < 1 || mo > 12 || d < 1 || d > 31) return { kind: 'invalid' };
    const dt = new Date(y, mo - 1, d); // LOCAL construction. Never new Date(s).
    if (Number.isNaN(dt.getTime()) || dt.getMonth() !== mo - 1 || dt.getDate() !== d) {
      return { kind: 'invalid' };
    }
    return { kind: 'calendar', date: dt };
  }

  const dt = new Date(s);
  return Number.isNaN(dt.getTime()) ? { kind: 'invalid' } : { kind: 'instant', date: dt };
}

function render(value, opts, emptyValue) {
  const c = classifyDateInput(value);
  if (c.kind === 'empty' || c.kind === 'invalid') return emptyValue;
  // A calendar date is rendered in the viewer's own zone, because its
  // components were constructed there. An instant is pinned to Eastern.
  const options = c.kind === 'calendar' ? opts : { ...opts, timeZone: EASTERN };
  return c.date.toLocaleDateString('en-US', options);
}

/**
 * Short display: "Aug 21". Returns the em-dash placeholder on empty/invalid,
 * preserving the contract every existing caller already depends on.
 */
export function formatDate(value, opts = SHORT) {
  return render(value, opts, '—');
}

/**
 * Human display: "Aug 21, 2026". Returns null on empty/invalid, preserving
 * the existing formatHumanDate contract (callers use `|| fallback`).
 */
export function formatHumanDate(value) {
  return render(value, HUMAN, null);
}

/**
 * License-status style: "Aug 21, 2026", em-dash placeholder on empty.
 * Same fact as formatHumanDate with a different empty contract.
 */
export function formatDateOrDash(value) {
  return render(value, HUMAN, '—');
}

/**
 * Short display with an EMPTY-STRING contract, for the surfaces that render
 * nothing rather than a placeholder when a date is absent.
 */
export function formatDateOrEmpty(value, opts = SHORT) {
  return render(value, opts, '');
}

/**
 * The parsed Date, or null. For DURATION helpers ("3d ago", "age in days"),
 * which are a different fact from date display but must not parse a calendar
 * string with `new Date` either. Use this instead of `new Date(value)`.
 */
export function toDate(value) {
  const c = classifyDateInput(value);
  return c.kind === 'calendar' || c.kind === 'instant' ? c.date : null;
}

/**
 * An INSTANT rendered with its time, pinned to Eastern. For timestamps only;
 * a calendar date has no time to show, so it falls back to the date alone.
 */
export function formatDateTime(value, opts = { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) {
  const c = classifyDateInput(value);
  if (c.kind === 'empty' || c.kind === 'invalid') return '';
  if (c.kind === 'calendar') return c.date.toLocaleDateString('en-US', HUMAN);
  return c.date.toLocaleString('en-US', { ...opts, timeZone: EASTERN });
}

/**
 * Whole days from today to target. Negative = past.
 *
 * For a CALENDAR date this counts CALENDAR DAYS between local midnights, not
 * milliseconds divided by 86400000. The old millisecond form compared a UTC
 * midnight against a local `now` and crossed a boundary at the wrong hour,
 * so a licence expiring today could read as expiring yesterday.
 *
 * Per CLAUDE.md "Pending UX" this returns the raw number; callers decide
 * whether to print "PAST DUE" instead of a negative.
 */
export function daysUntil(value, now = new Date()) {
  const c = classifyDateInput(value);
  if (c.kind === 'empty' || c.kind === 'invalid') return null;
  if (c.kind === 'calendar') {
    const a = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const b = new Date(c.date.getFullYear(), c.date.getMonth(), c.date.getDate());
    return Math.round((b - a) / 86400000);
  }
  return Math.ceil((c.date.getTime() - now.getTime()) / 86400000);
}

/**
 * A Date rendered back to a bare YYYY-MM-DD using its LOCAL components.
 *
 * `new Date().toISOString().slice(0, 10)` is the writer-side twin of the bug
 * this module exists to kill: after 8 PM Eastern it returns TOMORROW, so a
 * "due in 3 days" default silently became 4. Local components round-trip the
 * day the broker is actually looking at.
 */
export function toYMD(value = new Date()) {
  const d = value instanceof Date ? value : toDate(value);
  if (!d || Number.isNaN(d.getTime())) return null;
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * Today plus n days, as a bare YYYY-MM-DD in the viewer's own calendar.
 */
export function ymdFromNow(days = 0, now = new Date()) {
  return toYMD(new Date(now.getFullYear(), now.getMonth(), now.getDate() + days));
}

/**
 * Time of day for an INSTANT, pinned to Eastern ("2:45 PM"). Used by the
 * "as of / refreshed at" stamps, which are real server instants.
 */
export function formatTimeOfDay(value, opts = { hour: 'numeric', minute: '2-digit' }) {
  const c = classifyDateInput(value);
  if (c.kind !== 'instant') return '';
  return c.date.toLocaleTimeString('en-US', { ...opts, timeZone: EASTERN });
}

